/***
 *
 *   CHECKBOX
 *   Checkbox toggle used within the form
 *   Props are passed from the fieldset
 *
 *   PROPS
 *   callback: executed on change (function, required)
 *   checked: toggle the checkmark (boolean, required)
 *   className: custom styling (SCSS or tailwind style, optional)
 *   index: index of checkbox in fieldset (integer, required)
 *   name: name of the input to be used as the label (string, required)
 *   option: individual option passed from the fieldset (string, required)
 *   required: show the required * in the label (boolean, optional)
 *
 **********/

import { useEffect, useRef } from 'react';
import { Label, ClassHelper } from 'components/lib';
import Style from './checkbox.tailwind.js';

interface CheckboxProps {
  callback: (index: number, checked: boolean, option?: string) => void;
  checked: boolean;
  className?: string;
  index: number;
  name?: string;
  option?: string;
  required?: boolean;
  id?: string;
  labelText?: string;
  indeterminate?: boolean;
}

export function Checkbox(props: CheckboxProps) {
  const inputId = props.id || props.option;
  const labelText = props.labelText ?? props.option;
  const inputRef = useRef<HTMLInputElement>(null);
  const css = ClassHelper(Style, {
    input: true,
    className: props.className,
  });

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !!props.indeterminate;
    }
  }, [props.indeterminate]);

  return (
    <div>
      <input
        type="checkbox"
        name={props.name}
        id={inputId}
        value={props.option}
        className={css}
        checked={!!props.checked}
        ref={inputRef}
        onChange={() => props.callback(props.index, props.checked, props.option)}
      />

      {labelText && <Label text={labelText} required={props.required} for={inputId} className={Style.label} />}
    </div>
  );
}
